import { useCallback } from 'react';

import { useApiBeneficiariesList } from '@/entities/beneficiary';
import { useScenarioLazy } from '@/entities/scenario/hooks/useScenario';
import { useScenariosStore } from '@/entities/scenario/scenarios.store';
import { useNotices } from '@/features/notices/useNotices';
import { useApi } from '@/shared/api/hooks/useApi';
import { ScenarioStatus } from '@/shared/api/types';
import { useGetBeneficiariesLazyQuery } from '@/shared/generated/graphql';
import { AccountStatus } from '@/shared/generated/sdk';
import { useSJDatabase } from '@/shared/lib/sj-orm/hooks/use-sj-database';
import { useKeyPairStore } from '@/shared/store/decrypted-keypair.store';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';
import { log } from '@/shared/utils/log';

import {
  createTestAssets,
  createTestScenario,
  errorCase,
  getAssetsDtos,
  getMappedAssetsToPublicKeysResponses,
} from '../helpers';

export const useScenariosActions = () => {
  const sjDb = useSJDatabase();
  const { t } = useTranslation('widgets/scenariosActions', { keyPrefix: 'notices' });
  const { getScenario } = useScenarioLazy();
  const { removeScenario: removeScenarioFromCache, updateScenario: updateScenarioCache } =
    useScenariosStore();
  const { map: beneficiaryMap } = useApiBeneficiariesList();
  const notice = useNotices();

  const { keyPair } = useKeyPairStore();
  const [getBeneficiaries] = useGetBeneficiariesLazyQuery();

  const api = useApi();

  const findByDatabaseId = useCallback(
    (scenarioDatabaseId: string) => {
      return sjDb.collections.scenarioMetaInfos?.findOne(
        (scenario) => scenario.centralDbId === scenarioDatabaseId,
      );
    },
    [sjDb.collections.scenarioMetaInfos],
  );

  const findByDtoId = useCallback(
    (dtoId: string) => sjDb.collections.scenarioMetaInfos?.findById(dtoId),
    [sjDb.collections.scenarioMetaInfos],
  );

  const deleteScenario = useCallback(async (dbId: string) => {
    const scenarioMetaInfo = findByDatabaseId(dbId);
    if (!scenarioMetaInfo) {
      notice.error(t('scenarioNotFound'));
      return false;
    }

    const scenarioDbId = scenarioMetaInfo.centralDbId;
    const scenarioDtoId = scenarioMetaInfo.id;

    const scenario = await getScenario({ scenarioDbId, cached: false });

    if (scenario?.status !== ScenarioStatus.Draft) {
      notice.error(t('delete.incorrectStatus'));
      return false;
    }

    const { error } = await api.scenarioDelete(scenarioDbId);

    if (error) {
      notice.error(`${t('scenarioError')}: ${error.message}`);
      return false;
    }

    sjDb.collections.scenarioMetaInfos?.removeById(scenarioDtoId);
    removeScenarioFromCache(scenarioDbId);

    return true;
  }, []);

  const activateScenario = useCallback(
    async (dbId: string) => {
      if (!beneficiaryMap) {
        log.error('beneficiaryMap not ready');
        return false;
      }

      const scenario = await getScenario({ scenarioDbId: dbId, cached: false });

      if (scenario?.status !== ScenarioStatus.Draft) {
        notice.error(t('activate.incorrectStatus'));
        return false;
      }

      const notActiveBeneficiaries = scenario.transfers.filter((transfer) => {
        const beneficiaryDbId = transfer.recipientProfileId;

        const status = beneficiaryMap[beneficiaryDbId].status;

        return status !== AccountStatus.Active;
      });

      if (notActiveBeneficiaries.length) {
        notice.error(t('activate.familyMemberNotRegistered'));
        return false;
      }

      const { error, data } = await api.scenarioActivate(dbId);

      if (error || !data) {
        notice.error(`${t('scenarioError')}: ${error?.message}`);
        return false;
      }

      updateScenarioCache(data);

      return true;
    },
    [beneficiaryMap],
  );

  const deactivateScenario = useCallback(async (dbId: string) => {
    const scenario = await getScenario({ scenarioDbId: dbId, cached: false });

    if (scenario?.status !== ScenarioStatus.Active) {
      notice.error(t('deactivate.incorrectStatus'));
      return false;
    }

    const { error, data } = await api.scenarioCancel(dbId);

    if (error || !data) {
      notice.error(`${t('scenarioError')}: ${error?.message}`);
      return false;
    }

    updateScenarioCache(data);

    return true;
  }, []);

  const deleteManyScenarios = useCallback(async (dbIds: string[]) => {
    const promiseList = dbIds.map((scenarioDbId) => deleteScenario(scenarioDbId));
    const responses = await Promise.allSettled(promiseList);

    const hasFailedPromises = responses.some((resp) => !resp);

    if (hasFailedPromises) {
      return false;
    }

    return true;
  }, []);

  const testScenario = useCallback(
    async (dbId: string): Promise<boolean> => {
      const publicKey = keyPair?.publicKey;
      const scenario = await getScenario({ scenarioDbId: dbId, cached: false });

      if (!publicKey) return false;
      if (!scenario) return errorCase(t('scenarioError'), 'The scenario was not found', notice);
      if (
        !(scenario.status === ScenarioStatus.Draft || scenario.status === ScenarioStatus.Active)
      ) {
        notice.error(t('test.incorrectStatus'));
        return false;
      }

      const assetsDtos = await getAssetsDtos(scenario, sjDb);

      if (!assetsDtos.length) {
        return errorCase(t('scenarioError'), 'There is no any asset related the Scenario', notice);
      }

      const { testAssets, testDtoIds } = await createTestAssets(assetsDtos, sjDb, publicKey);

      // amount of test assets DTOs IDs, should be equal test created assets
      if (testAssets.length !== testDtoIds.length) {
        return errorCase(t('scenarioError'), 'Test asset creation error', notice);
      }

      const testScenarioResponse = await createTestScenario({
        scenario,
        sjDb,
        publicKey,
        api,
        testDtoIds,
        findByDatabaseId,
      });

      if (!(testScenarioResponse.ok && testScenarioResponse.data?.id)) {
        return errorCase(
          t('scenarioError'),
          testScenarioResponse.error?.message ?? 'Scenario creation error',
          notice,
        );
      }

      log.debug('Test scenario is created');

      const recipientProfileIds = scenario.transfers.map((transfer) => transfer.recipientProfileId);

      const mappedAssetsToPublicKeysResponses = await getMappedAssetsToPublicKeysResponses({
        getBeneficiaries,
        recipientProfileIds,
        testAssets,
        sjDb,
      });

      if (!mappedAssetsToPublicKeysResponses.every(Boolean)) {
        return errorCase(
          t('scenarioError'),
          'Assets were not mapped to public beneficiary keys',
          notice,
        );
      }

      const isActiveScenario = await activateScenario(testScenarioResponse.data.id);

      if (!isActiveScenario) {
        return false;
      }

      log.debug('Test scenario is active');

      const scenarioLaunchResponse = await api.scenarioRunImmediately(testScenarioResponse.data.id);

      if (!scenarioLaunchResponse.ok) {
        return errorCase(t('scenarioError'), 'Test scenario was not launched', notice);
      }

      log.debug('Test scenario was launched');

      // await createNotification({
      //   type: NotificationType.TEST_SCENARIO_LAUNCHED,
      //   scenarioId: scenario.id,
      // });

      return true;
    },
    [getScenario, keyPair, sjDb, api, findByDatabaseId, getBeneficiaries, log, activateScenario],
  );

  return {
    findByDatabaseId,
    findByDtoId,
    deleteScenario,
    activateScenario,
    deactivateScenario,
    deleteManyScenarios,
    testScenario,
  };
};
